/**
 * Backend selection. Three's own WebGPU->WebGL fallback logs a console warning, so the choice is
 * made here, up front, and the renderer is constructed with an explicit `forceWebGL`.
 */

export type BackendName = 'webgpu' | 'webgl2';

export interface BackendDecisionInputs {
  hasGpu: boolean;
  adapterFound: boolean;
  forceWebGL: boolean;
  override: BackendName | null;
}

export function chooseBackend(i: BackendDecisionInputs): BackendName {
  if (i.override === 'webgl2') return 'webgl2';
  if (i.forceWebGL && i.override !== 'webgpu') return 'webgl2';
  return i.hasGpu && i.adapterFound ? 'webgpu' : 'webgl2';
}

export function parseBackendOverride(search: string): BackendName | null {
  const value = new URLSearchParams(search).get('backend');
  return value === 'webgl2' || value === 'webgpu' ? value : null;
}

/** Asks the browser for a WebGPU adapter once. Returns false when WebGPU is absent or refused. */
export async function probeWebGPU(): Promise<{ hasGpu: boolean; adapterFound: boolean }> {
  const gpu = typeof navigator === 'undefined' ? undefined : navigator.gpu;
  if (!gpu) return { hasGpu: false, adapterFound: false };
  try {
    const adapter = await gpu.requestAdapter();
    return { hasGpu: true, adapterFound: adapter !== null };
  } catch {
    return { hasGpu: true, adapterFound: false };
  }
}
